import { TitleCasePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslocoService } from '@ngneat/transloco';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, concatMap, finalize, map, take, tap } from 'rxjs/operators';
import { ErrorHandlerService } from 'src/app/core/api/error-handler.service';
import { ConfirmModalService } from 'src/app/shared/confirm-modal/confirm-modal.service';
import { PatientPortalGrantExportDestination } from 'src/API';
import { AssessmentService } from 'src/app/core/api/assessment.service';
import { Html2PdfService } from 'src/app/core/api/html-2-pdf.service';
import { Patient } from 'src/app/core/api/patient.service';
import { AssessmentBody } from 'src/app/econsult/assessment-body/assessment-body.model';
import { ReferralPdfServiceService } from 'src/app/referral/referral-pdf-service.service';
import { ContentType } from 'src/app/shared/shared-pipes/image-to-data-uri.pipe';
import { PDFType } from './dry-eye-pdf-export.component';
import {
  ExportDestination,
  ExportDestinations
} from './pdf-export-button/pdf-export-button.component';
import { PDFExportService } from './pdf-export.service';

export interface AssessmentPdfSendRequest {
  assessmentBody: AssessmentBody;
  clinic: any;
  clinicName: string;
  clinicId: string;
  assessmentId: string;
  reportType: string;
  patient: Patient;
  pdfType: PDFType;
  lang: string;
  destination: ExportDestination;
}

const PATIENT_PORTAL_LINK_LIFETIME_IN_MILLISECONDS = 30 * 24 * 60 * 60 * 1000;
const MAXIMUM_OTP_ATTEMPTS = 4;

@Injectable({
  providedIn: 'root'
})
export class AssessmentPdfSendService {
  private readonly titleCasePipe = new TitleCasePipe();
  private readonly isExportingSubject = new BehaviorSubject<boolean>(false);

  public readonly isExporting$: Observable<boolean> = this.isExportingSubject.asObservable();

  constructor(
    private pdfExportService: PDFExportService,
    private html2PdfService: Html2PdfService,
    private referralPdfService: ReferralPdfServiceService,
    private assessmentService: AssessmentService,
    private confirmModalService: ConfirmModalService,
    private translocoService: TranslocoService,
    private errorHandlerService: ErrorHandlerService,
    private snackBar: MatSnackBar
  ) {}

  /**
   * Both exports subscribe here, not in the caller. A side sheet that closes mid-flight would
   * otherwise unsubscribe and abandon a send the server has already begun — the patient still
   * receives the report, while the clinician never sees the SENT/FAILED snackbar.
   */
  public runDownload(
    assessmentBody: AssessmentBody,
    clinic: any,
    pdfType: PDFType,
    lang: string
  ): void {
    this.isExportingSubject.next(true);
    this.pdfExportService
      .downloadPDFForAssessmentBody(assessmentBody, clinic, pdfType, false, lang)
      .pipe(
        take(1),
        catchError(error => {
          this.errorHandlerService.handleGraphQLError(error, true);
          return of(null);
        }),
        finalize(() => this.isExportingSubject.next(false))
      )
      .subscribe();
  }

  public runSend(request: AssessmentPdfSendRequest): void {
    this.isExportingSubject.next(true);
    this.sendAndNotify(request)
      .pipe(
        take(1),
        catchError(() => of(null)),
        finalize(() => this.isExportingSubject.next(false))
      )
      .subscribe();
  }

  public confirmSend(patient: Patient, destination: ExportDestination): Observable<boolean> {
    const patientName = this.titleCasePipe.transform(
      `${patient.firstName} ${patient.lastName}`
    );
    const title = this.translocoService.translate('Send Report to {{patientName}}?', {
      patientName
    });
    const body =
      destination === ExportDestinations.PatientApp
        ? this.translocoService.translate(
            'Are you sure you want to send this report to {{patientName}} on the Patient App?',
            { patientName }
          )
        : this.translocoService.translate(
            'Are you sure you want to send this report to {{patientName}} at {{email}}?',
            { patientName, email: patient.email }
          );

    return this.confirmModalService
      .show(
        title,
        body,
        this.translocoService.translate('Send'),
        this.translocoService.translate('Cancel')
      )
      .pipe(take(1), map(confirmed => !!confirmed));
  }

  public sendAndNotify(request: AssessmentPdfSendRequest): Observable<any> {
    const snackbarParams =
      request.destination === ExportDestinations.PatientApp
        ? {}
        : { email: request.patient.email };

    return this.sendToPatient(request).pipe(
      tap({
        next: () => this.showSendSnackbar('SENT', request.destination, snackbarParams),
        error: () => this.showSendSnackbar('FAILED', request.destination, snackbarParams)
      })
    );
  }

  private showSendSnackbar(
    outcome: 'SENT' | 'FAILED',
    destination: ExportDestination,
    snackbarParams: { email?: string }
  ): void {
    const channelKey =
      destination === ExportDestinations.PatientApp ? 'PATIENT_APP' : 'EMAIL';
    this.openSnackbar(
      this.translocoService.translate(`SNACKBARS.${channelKey}_${outcome}`, snackbarParams)
    );
  }

  private openSnackbar(message: string): void {
    this.snackBar.open(message, this.translocoService.translate('SNACKBARS.CLOSE'), {
      duration: 4000
    });
  }

  public buildFilename(patient: Patient): string {
    return `${patient.firstName}--${
      patient.lastName
    }--${this.pdfExportService.getDateFileName()}.pdf`;
  }

  public sendToPatient(request: AssessmentPdfSendRequest): Observable<any> {
    const filename = this.buildFilename(request.patient);

    return this.pdfExportService
      .getHtmlForAssessmentBody(
        request.assessmentBody,
        request.clinic,
        request.pdfType,
        false,
        false,
        request.lang
      )
      .pipe(
        concatMap((html: string) => this.html2PdfService.html2Pdf(html)),
        concatMap(htmlBase64 => {
          const createFile = true;
          const attachmentFile: File = this.referralPdfService.base64ToBlob(
            htmlBase64.base64,
            ContentType.PDF,
            filename,
            createFile
          ) as File;

          return this.assessmentService.createPatientPortalGrantAccess({
            filename: filename,
            patientId: request.patient.id,
            patientPortalGrantClinicId: request.clinicId,
            reportType: request.reportType,
            assessmentId: request.assessmentId,
            attachmentFile,
            linkExpiresAt: new Date(
              Date.now() + PATIENT_PORTAL_LINK_LIFETIME_IN_MILLISECONDS
            ).toISOString(),
            maxOTPAttempts: MAXIMUM_OTP_ATTEMPTS,
            failedOTPAttempts: 0,
            accessCount: 0,
            revoked: false,
            createdAt: new Date().toISOString(),
            ...(request.destination === ExportDestinations.PatientApp && {
              exportDestination: PatientPortalGrantExportDestination.patientApp
            })
          });
        })
      );
  }
}
